import { ObservableQuery } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router";
import InfiniteLoader from "react-window-infinite-loader";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import FiltersSortHeaderStaticColumn from "components/Filters/SortHeader/StaticColumn";
import FiltersSortHeaderWrapper from "components/Filters/SortHeader/Wrapper";
import FlashContext from "components/FlashMessages/FlashContext";
import HideOnScrollProvider from "components/HideOnScroll/HideOnScrollContext";
import { EmptystateWebhooksColored } from "components/icons/generated";
import ListEntitiesNew from "components/ListEntitiesNew";
import PageLoading from "components/loading/PageLoading";
import { WebhooksTierInfo } from "components/TierInfo/WebhooksTierInfo";
import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Box from "ds/components/Box";
import EmptyState from "ds/components/EmptyState";
import Typography from "ds/components/Typography";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { FILTERS_ORDER_SETTINGS_KEY } from "views/Account/ModulesNew/constants";

import { ModuleSettingsContext } from "../Context";
import { COLUMN_ORDER } from "./components/WebhookDeliveryListItem/constants";
import WebhookDeliveryListItemVirtualized from "./components/WebhookDeliveryListItem/Virtualized";
import { GET_MODULE_WEBHOOK } from "./gql";
import { GetModuleWebhookDeliveriesGql } from "./types";

const ModuleSettingsIntegrationsWebhook = () => {
  const { onError } = useTypedContext(FlashContext);
  const { module } = useTypedContext(ModuleSettingsContext);
  const { webhookId } = useParams<{ webhookId: string }>();

  useTitle(module?.name ? `Recent deliveries · ${module.name}` : "Recent deliveries");

  useBreadcrumbs(
    [
      {
        title: "Modules",
        link: getBreadcrumbsBackUrl("/modules", FILTERS_ORDER_SETTINGS_KEY),
      },
      {
        title: module?.name || "",
        link: `/module/${module?.id}`,
      },
      {
        title: "Module settings",
        link: `/module/${module?.id}/settings/integrations/webhooks`,
      },
      {
        title: "Recent deliveries",
      },
    ],
    [module?.name, module?.id]
  );

  const { loading, data, fetchMore } = useQuery(GET_MODULE_WEBHOOK, {
    variables: {
      id: webhookId ?? "",
      moduleId: module.id,
    },
    skip: !webhookId,
    nextFetchPolicy: "cache-first",
  });

  const token = data?.module?.integrations?.webhook?.deliveries?.nextToken;

  const deliveries = useMemo(
    () => data?.module?.integrations?.webhook?.deliveries?.nodes || [],
    [data?.module?.integrations?.webhook?.deliveries?.nodes]
  );

  const updateQuery: ObservableQuery.FetchMoreOptions<
    GetModuleWebhookDeliveriesGql,
    { moduleId: string; id: string; nextToken?: string | null }
  >["updateQuery"] = (prev, { fetchMoreResult }) => {
    if (!fetchMoreResult) return prev;

    if (
      prev?.module?.integrations?.webhook?.deliveries?.nodes &&
      fetchMoreResult?.module?.integrations?.webhook?.deliveries?.nodes
    ) {
      return {
        module: {
          ...fetchMoreResult.module,
          integrations: {
            ...fetchMoreResult.module.integrations,
            webhook: {
              ...fetchMoreResult.module.integrations.webhook,
              deliveries: {
                ...fetchMoreResult.module.integrations.webhook.deliveries,
                nextToken: fetchMoreResult.module.integrations.webhook.deliveries.nextToken,
                nodes: [
                  ...(prev.module.integrations.webhook.deliveries.nodes || []),
                  ...(fetchMoreResult.module.integrations.webhook.deliveries.nodes || []),
                ],
              },
            },
          },
        },
      };
    }
    return { module: { ...prev.module } };
  };

  const loadMoreItems = useCallback(async () => {
    if (!token || loading) return;
    try {
      await fetchMore({
        variables: { nextToken: token },
        updateQuery,
      });
    } catch (error) {
      onError(error);
    }
  }, [fetchMore, onError, token, loading]);

  const isItemLoaded = (value: number) => value < deliveries.length;

  return (
    <HideOnScrollProvider>
      <ViewHeader>
        <ViewHeaderWrapper>
          <ViewHeaderTitle>Recent deliveries</ViewHeaderTitle>
        </ViewHeaderWrapper>
      </ViewHeader>
      <WebhooksTierInfo />

      {loading && !data && <PageLoading />}

      {deliveries.length > 0 && (
        <>
          <FiltersSortHeaderWrapper columnOrder={COLUMN_ORDER}>
            <FiltersSortHeaderStaticColumn>
              <Typography tag="span" variant="p-t7">
                Delivery ID
              </Typography>
            </FiltersSortHeaderStaticColumn>

            <FiltersSortHeaderStaticColumn>
              <Typography tag="span" variant="p-t7">
                Status code
              </Typography>
            </FiltersSortHeaderStaticColumn>
            <FiltersSortHeaderStaticColumn>
              <Typography tag="span" variant="p-t7">
                Delivered at
              </Typography>
            </FiltersSortHeaderStaticColumn>
          </FiltersSortHeaderWrapper>

          <InfiniteLoader
            isItemLoaded={isItemLoaded}
            itemCount={deliveries.length + 50}
            loadMoreItems={loadMoreItems}
          >
            {({ onItemsRendered }) => (
              <ListEntitiesNew
                itemCount={deliveries.length}
                itemProps={{
                  items: deliveries,
                }}
                virtualizedItem={WebhookDeliveryListItemVirtualized}
                itemKey={(index) => deliveries[index].id}
                onItemsRendered={onItemsRendered}
              />
            )}
          </InfiniteLoader>
        </>
      )}

      {deliveries.length === 0 && !loading && (
        <Box align="center" justify="center" grow="1" fullWidth>
          <EmptyState padding="large" icon={EmptystateWebhooksColored} title="No deliveries yet" />
        </Box>
      )}
    </HideOnScrollProvider>
  );
};

export default ModuleSettingsIntegrationsWebhook;
